import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Non-sensitive identity snapshot cached so the app survives an offline
// reload — the actual session cookie is httpOnly and unchanged. Only
// contains fields the UI needs to decide "authed vs. not" and to route
// (id, role, name, email, avatar_url). Cleared on explicit logout.
const AUTH_CACHE_KEY = "cv_last_user:v1";

function readCachedUser() {
  try {
    const raw = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.id && parsed.role) return parsed;
  } catch { /* corrupt or private tab */ }
  return null;
}

function writeCachedUser(u) {
  try {
    if (!u) {
      window.localStorage.removeItem(AUTH_CACHE_KEY);
      return;
    }
    // Whitelist fields — never persist a token or anything sensitive.
    const snapshot = {
      id: u.id,
      role: u.role,
      name: u.name,
      email: u.email,
      avatar_url: u.avatar_url,
      email_verified: u.email_verified,
    };
    window.localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(snapshot));
  } catch { /* quota / private tab */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=no auth, obj=authed
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    // The httpOnly access_token cookie (if present) tells us who we are.
    api
      .get("/auth/me")
      .then((res) => {
        if (!mounted) return;
        setUser(res.data);
        writeCachedUser(res.data);
      })
      .catch(() => {
        if (!mounted) return;
        // If we're offline AND we have a last-known identity, keep the
        // viewer signed in for the session. The SW intentionally never
        // caches /auth/me (a stale copy could grant offline access on a
        // shared device) so this is our only survival path.
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        const cached = offline ? readCachedUser() : null;
        setUser(cached || false);
      })
      .finally(() => {
        if (mounted) setReady(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // The session cookie was set by the backend on this same response. We
    // never read or persist the JWT in JavaScript — keeping it off the page
    // means XSS can't lift it.
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      writeCachedUser(me.data);
      return me.data;
    } catch (err) {
      // /auth/me failed right after a successful login — log it for
      // observability and fall back to the login payload so the UI still works.
      console.warn("[auth] /auth/me failed after login, using login payload", err);
      setUser(data);
      writeCachedUser(data);
      return data;
    }
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // Registration no longer establishes a session — the user must confirm
    // their email first. Callers handle the verification UX themselves.
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout endpoint failure is non-fatal — we still clear local state.
      console.warn("[auth] /auth/logout request failed", err);
    }
    setUser(false);
    writeCachedUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
